// Phase 26E.1 — Shared ERP-style grid: filter bar + checkbox grid + bulk-action bar.
// Used by Admin Order Grid (26E.1) and Admin Claim Grid (26E.2).
//
// Contract summary (see route brief for the full spec):
//   props.filters          array of filter-field descriptors
//   props.columns          [{key, label, render?, sortable?, align?, width?}]
//   props.onLoad(query)    async → { rows, total, page_size }
//   props.bulkActions      [{key, label, icon, appliesTo(rows), onExecute(rows),
//                             requiresPrompt?, promptSchema?}]
//   props.onRowClick(row)
//   props.rowKey           string | (row)=>string (default: row.id)
//   props.testid           test-id prefix (default: "bulk-grid")
//
// Selection is a Set of row ids; persists across pagination.
// Server-side filter + sort + pagination; nothing filtered client-side.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, RefreshCcw, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { formatApiError } from "@/lib/api";

const rowKeyFn = (row, keyProp) =>
  typeof keyProp === "function" ? keyProp(row) : (row?.[keyProp || "id"] ?? "");

export default function BulkActionGrid({
  filters = [], columns = [], onLoad, bulkActions = [], onRowClick,
  rowKey = "id", testid = "bulk-grid",
  initialFilterValues = {}, initialSort = { by: null, dir: "desc" },
  pageSizeOptions = [20, 50, 100],
}) {
  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [sort, setSort] = useState(initialSort);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selection, setSelection] = useState(new Set()); // row ids across pages
  const [selectedRows, setSelectedRows] = useState(new Map()); // id → row payload

  const query = useMemo(() => ({
    ...filterValues, page, page_size: pageSize,
    sort_by: sort.by || undefined, sort_dir: sort.dir,
  }), [filterValues, page, pageSize, sort]);

  const doLoad = useCallback(async () => {
    if (!onLoad) return;
    setBusy(true); setError(null);
    try {
      const res = await onLoad(query);
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(formatApiError(e));
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }, [onLoad, query]);

  useEffect(() => { doLoad(); }, [doLoad]);

  const currentPageKeys = useMemo(() =>
    rows.map(r => rowKeyFn(r, rowKey)), [rows, rowKey]);
  const allCurrentSelected = currentPageKeys.length > 0 &&
    currentPageKeys.every(k => selection.has(k));
  const someCurrentSelected = currentPageKeys.some(k => selection.has(k));

  const toggleAllCurrent = () => {
    const next = new Set(selection);
    const nextMap = new Map(selectedRows);
    if (allCurrentSelected) {
      currentPageKeys.forEach(k => { next.delete(k); nextMap.delete(k); });
    } else {
      rows.forEach(r => {
        const k = rowKeyFn(r, rowKey);
        next.add(k); nextMap.set(k, r);
      });
    }
    setSelection(next); setSelectedRows(nextMap);
  };

  const toggleRow = (row) => {
    const k = rowKeyFn(row, rowKey);
    const next = new Set(selection);
    const nextMap = new Map(selectedRows);
    if (next.has(k)) { next.delete(k); nextMap.delete(k); }
    else { next.add(k); nextMap.set(k, row); }
    setSelection(next); setSelectedRows(nextMap);
  };

  const clearSelection = () => { setSelection(new Set()); setSelectedRows(new Map()); };

  // Bulk action prompt state
  const [prompt, setPrompt] = useState(null); // {action, values}
  const runBulkAction = async (action) => {
    const selRows = Array.from(selectedRows.values());
    if (selRows.length === 0) { toast.error("Select at least one row"); return; }
    if (action.appliesTo && !action.appliesTo(selRows)) {
      toast.error(`No selected rows are eligible for “${action.label}”`);
      return;
    }
    if (action.requiresPrompt) {
      setPrompt({ action, values: Object.fromEntries((action.promptSchema || []).map(f => [f.key, f.default ?? ""])) });
      return;
    }
    await execute(action, null);
  };

  const execute = async (action, promptValues) => {
    const selRows = Array.from(selectedRows.values());
    setBusy(true);
    try {
      const result = await action.onExecute(selRows, promptValues);
      if (result?.toast) toast.success(result.toast);
      clearSelection();
      setPrompt(null);
      await doLoad();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const pageMax = Math.max(1, Math.ceil(total / pageSize));
  const goto = (p) => setPage(Math.min(pageMax, Math.max(1, p)));

  return (
    <div className="space-y-3" data-testid={`${testid}-root`}>
      {/* Filter bar */}
      {filters.length > 0 && (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-3">
            <div className="flex flex-wrap items-start gap-3" data-testid={`${testid}-filters`}>
              {filters.map(f => (
                <FilterField
                  key={f.key} spec={f}
                  value={filterValues[f.key]}
                  onChange={(v) => { setFilterValues(prev => ({ ...prev, [f.key]: v })); setPage(1); }}
                  testid={`${testid}-filter-${f.key}`}
                />
              ))}
              <div className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={doLoad} disabled={busy}
                        data-testid={`${testid}-refresh`}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk action bar */}
      {selection.size > 0 && (
        <div
          className="sticky top-2 z-20 rounded-2xl border-2 border-[#6D2C91] bg-white shadow-lg px-4 py-2 flex items-center gap-2 flex-wrap"
          data-testid={`${testid}-bulk-bar`}
        >
          <span className="text-sm font-semibold text-[#6D2C91]"
                data-testid={`${testid}-selection-count`}>
            {selection.size} selected
          </span>
          <button onClick={clearSelection}
                  data-testid={`${testid}-clear-selection`}
                  className="text-xs text-neutral-500 hover:text-neutral-900">Clear</button>
          <div className="mx-2 h-6 w-px bg-neutral-200" />
          {bulkActions.map(a => {
            const selRows = Array.from(selectedRows.values());
            const applicable = !a.appliesTo || a.appliesTo(selRows);
            return (
              <Button key={a.key} size="sm"
                      onClick={() => runBulkAction(a)}
                      disabled={!applicable || busy}
                      variant={a.destructive ? "destructive" : "default"}
                      className={a.destructive ? "" : "tulsi-gradient text-white"}
                      data-testid={`${testid}-action-${a.key}`}>
                {a.icon ? <a.icon className="w-3.5 h-3.5 mr-1" /> : null}
                {a.label}
                {a.count && <span className="ml-1 text-xs opacity-80">({a.count(selRows)})</span>}
              </Button>
            );
          })}
        </div>
      )}

      {/* Grid */}
      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[560px]">
            <table className="w-full text-xs" data-testid={`${testid}-table`}>
              <thead className="bg-[#6D2C91] text-white sticky top-0 z-10">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <Checkbox
                      checked={allCurrentSelected ? true : (someCurrentSelected ? "indeterminate" : false)}
                      onCheckedChange={toggleAllCurrent}
                      data-testid={`${testid}-select-all`}
                    />
                  </th>
                  {columns.map(c => {
                    const activeSort = sort.by === c.key;
                    return (
                      <th key={c.key}
                          onClick={() => c.sortable && setSort(s => ({
                            by: c.key,
                            dir: (s.by === c.key && s.dir === "desc") ? "asc" : "desc",
                          }))}
                          className={
                            "text-left px-3 py-2 font-semibold whitespace-nowrap " +
                            (c.align === "right" ? "text-right " : "") +
                            (c.sortable ? "cursor-pointer select-none " : "")
                          }
                          style={c.width ? { width: c.width } : undefined}
                          data-testid={`${testid}-col-${c.key}`}>
                        <span className="inline-flex items-center gap-1">
                          {c.label}
                          {c.sortable && activeSort && (sort.dir === "desc"
                            ? <ArrowDown className="w-3 h-3" />
                            : <ArrowUp className="w-3 h-3" />)}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {busy && rows.length === 0 ? (
                  <tr><td colSpan={columns.length + 1} className="text-center p-8 text-neutral-500">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
                  </td></tr>
                ) : error ? (
                  <tr><td colSpan={columns.length + 1} className="text-center p-8 text-red-600">
                    {error}
                  </td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={columns.length + 1} className="text-center p-8 text-neutral-500">
                    No records match the current filters.
                  </td></tr>
                ) : rows.map((row) => {
                  const k = rowKeyFn(row, rowKey);
                  const checked = selection.has(k);
                  return (
                    <tr key={k}
                        className={
                          "border-t hover:bg-neutral-50 " +
                          (checked ? "bg-purple-50 " : "") +
                          (onRowClick ? "cursor-pointer" : "")
                        }
                        data-testid={`${testid}-row-${k}`}>
                      <td className="w-10 px-3 py-2" onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleRow(row)}
                          data-testid={`${testid}-row-check-${k}`}
                        />
                      </td>
                      {columns.map(c => (
                        <td key={c.key}
                            onClick={() => onRowClick && onRowClick(row)}
                            className={
                              "px-3 py-2 whitespace-nowrap " +
                              (c.align === "right" ? "text-right font-mono tabular-nums" : "")
                            }
                            data-testid={`${testid}-cell-${k}-${c.key}`}>
                          {c.render ? c.render(row) : (row[c.key] ?? "—")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs" data-testid={`${testid}-pagination`}>
        <div className="text-neutral-500">
          {total > 0
            ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString("en-IN")}`
            : "—"}
          {selection.size > 0 && (
            <span className="ml-2 text-[#6D2C91]">· {selection.size} selected across pages</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-8 rounded border border-neutral-200 px-2 text-xs"
            data-testid={`${testid}-page-size`}>
            {pageSizeOptions.map(n => <option key={n} value={n}>{n}/page</option>)}
          </select>
          <Button size="icon" variant="outline" onClick={() => goto(1)} disabled={page === 1}
                  data-testid={`${testid}-page-first`}>
            <ChevronsLeft className="w-3.5 h-3.5" />
          </Button>
          <Button size="icon" variant="outline" onClick={() => goto(page - 1)} disabled={page === 1}
                  data-testid={`${testid}-page-prev`}>
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <div className="text-neutral-700 font-mono">
            {page} / {pageMax}
          </div>
          <Button size="icon" variant="outline" onClick={() => goto(page + 1)} disabled={page >= pageMax}
                  data-testid={`${testid}-page-next`}>
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
          <Button size="icon" variant="outline" onClick={() => goto(pageMax)} disabled={page >= pageMax}
                  data-testid={`${testid}-page-last`}>
            <ChevronsRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Bulk action prompt dialog */}
      {prompt && (
        <Dialog open={true} onOpenChange={(o) => !o && setPrompt(null)}>
          <DialogContent className="max-w-md" data-testid={`${testid}-prompt`}>
            <DialogHeader>
              <DialogTitle>{prompt.action.label}</DialogTitle>
              <DialogDescription>
                {prompt.action.promptDescription
                  || `${prompt.action.label} ${selection.size} selected row(s).`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {(prompt.action.promptSchema || []).map(field => (
                <div key={field.key} className="space-y-1">
                  <label className="text-xs font-semibold text-neutral-700">
                    {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  {field.type === "textarea" ? (
                    <textarea
                      value={prompt.values[field.key] ?? ""}
                      onChange={e => setPrompt(p => ({ ...p, values: { ...p.values, [field.key]: e.target.value } }))}
                      rows={3}
                      className="w-full border border-neutral-200 rounded px-2 py-1 text-sm"
                      placeholder={field.placeholder}
                      data-testid={`${testid}-prompt-${field.key}`}
                    />
                  ) : (
                    <Input
                      value={prompt.values[field.key] ?? ""}
                      onChange={e => setPrompt(p => ({ ...p, values: { ...p.values, [field.key]: e.target.value } }))}
                      placeholder={field.placeholder}
                      data-testid={`${testid}-prompt-${field.key}`}
                    />
                  )}
                  {field.hint && <div className="text-[10px] text-neutral-500">{field.hint}</div>}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPrompt(null)}
                      data-testid={`${testid}-prompt-cancel`}>Cancel</Button>
              <Button
                onClick={() => {
                  const missing = (prompt.action.promptSchema || []).filter(
                    f => f.required && !(prompt.values[f.key] || "").toString().trim());
                  if (missing.length) { toast.error(`${missing.map(f => f.label).join(", ")} required`); return; }
                  const validationError = (prompt.action.promptSchema || []).find(
                    f => f.minLength && (prompt.values[f.key] || "").toString().trim().length < f.minLength);
                  if (validationError) { toast.error(`${validationError.label} min ${validationError.minLength} chars`); return; }
                  execute(prompt.action, prompt.values);
                }}
                disabled={busy}
                className={prompt.action.destructive ? "" : "tulsi-gradient text-white"}
                variant={prompt.action.destructive ? "destructive" : "default"}
                data-testid={`${testid}-prompt-confirm`}>
                {busy ? "Working…" : prompt.action.confirmLabel || "Confirm"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// -------------------------------------------------------------- FilterField
// Renders one filter descriptor. Supported types:
//   text                → single input (server-side substring match)
//   date_range          → two date inputs pushed as `date_from`, `date_to`
//   checkbox_group      → multi-select flat list (pushes comma-joined string)
//   single_select       → dropdown
//   multi_select        → dropdown w/ checkboxes (compact)
function FilterField({ spec, value, onChange, testid }) {
  const { type, label, options, key } = spec;
  if (type === "text") {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-neutral-500">{label}</span>
        <Input value={value || ""} onChange={e => onChange(e.target.value)}
                placeholder={spec.placeholder} className="h-8 w-40"
                data-testid={testid} />
      </div>
    );
  }
  if (type === "date_range") {
    const val = value || {};
    return (
      <div className="flex items-center gap-1.5" data-testid={testid}>
        <span className="text-[11px] text-neutral-500">{label}</span>
        <input type="date" value={val.date_from || ""}
                onChange={e => onChange({ ...val, date_from: e.target.value })}
                className="h-8 rounded border border-neutral-200 px-1.5 text-xs"
                data-testid={`${testid}-from`} />
        <span className="text-neutral-400 text-xs">to</span>
        <input type="date" value={val.date_to || ""}
                onChange={e => onChange({ ...val, date_to: e.target.value })}
                className="h-8 rounded border border-neutral-200 px-1.5 text-xs"
                data-testid={`${testid}-to`} />
      </div>
    );
  }
  if (type === "checkbox_group") {
    const arr = Array.isArray(value) ? value : (value ? String(value).split(",") : []);
    const toggle = (opt) => {
      const next = arr.includes(opt) ? arr.filter(x => x !== opt) : [...arr, opt];
      onChange(next);
    };
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
        <span className="text-[11px] text-neutral-500">{label}:</span>
        {(options || []).map(o => (
          <label key={o.value}
                  className={
                    "text-[11px] cursor-pointer rounded-full border px-2 py-0.5 " +
                    (arr.includes(o.value) ? "bg-[#6D2C91] text-white border-[#6D2C91]" : "border-neutral-200 text-neutral-700 hover:bg-neutral-50")
                  }
                  data-testid={`${testid}-${o.value}`}>
            <input type="checkbox" checked={arr.includes(o.value)}
                    onChange={() => toggle(o.value)} className="hidden" />
            {o.label}
          </label>
        ))}
      </div>
    );
  }
  // single_select and multi_select
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <span className="text-[11px] text-neutral-500">{label}</span>
      <select
        value={value || ""}
        onChange={e => onChange(e.target.value || null)}
        className="h-8 rounded border border-neutral-200 px-2 text-xs bg-white min-w-32">
        <option value="">All</option>
        {(options || []).map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
